/**
 * 租户主题管理 Hook
 * 负责加载、缓存和应用租户主题配置
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { theme } from 'antd';
import type { ThemeConfig as AntdThemeConfig } from 'antd';
import {
  tenantThemeApi,
  type TenantThemeConfig,
  type UpdateTenantThemeRequest,
  type PresetTheme,
} from '@/modules/foundation/api/tenantTheme';
import { getToken, getUserInfo } from '@/utils/storage';
import { resolveUiSkin, syncUiSkinToLocal } from '@/theme/forest';
import { applyTenantFavicon, resolveTenantFaviconUrl } from '@/utils/favicon';
import { ossProxyUrl } from '@/utils/ossUpload';

const THEME_CACHE_KEY = 'tenant_theme_config';
const THEME_CACHE_EXPIRY_KEY = 'tenant_theme_config_expiry';
const CACHE_DURATION = 30 * 60 * 1000; // 30分钟

export interface UseTenantThemeResult {
  /** 当前主题配置 */
  themeConfig: TenantThemeConfig | null;
  /** 是否正在加载 */
  loading: boolean;
  /** 错误信息 */
  error: string | null;
  /** 预设主题列表 */
  presets: PresetTheme[];
  /** 更新主题配置 */
  updateTheme: (updates: UpdateTenantThemeRequest) => Promise<boolean>;
  /** 应用预设主题 */
  applyPreset: (presetCode: string) => Promise<boolean>;
  /** 重置为默认主题 */
  resetTheme: () => Promise<boolean>;
  /** 重新加载主题 */
  reloadTheme: () => Promise<void>;
  /** 生成 Ant Design 主题配置 */
  antdTheme: AntdThemeConfig;
}

/**
 * 将主题配置应用到文档
 * 注意：使用camelCase字段名，因为request.ts会自动将后端的snake_case转换为camelCase
 */
function applyThemeToDocument(config: TenantThemeConfig): void {
  if (typeof document === 'undefined' || !config) return;

  const root = document.documentElement;

  // 设置CSS变量（使用默认值防止 undefined）
  root.style.setProperty('--primary-color', config.primaryColor || '#1890ff');
  // 强调色：复用配置中原本未被消费的 secondaryColor（森林绿橙色 CTA 桥接）
  if (config.secondaryColor) {
    root.style.setProperty('--secondary-color', config.secondaryColor);
  } else {
    root.style.removeProperty('--secondary-color');
  }
  root.style.setProperty('--success-color', config.successColor || '#52c41a');
  root.style.setProperty('--warning-color', config.warningColor || '#faad14');
  root.style.setProperty('--error-color', config.errorColor || '#ff4d4f');
  root.style.setProperty('--border-radius', `${config.borderRadius ?? 6}px`);
  root.style.setProperty('--font-size', `${config.fontSize ?? 14}px`);
  root.style.setProperty('--sidebar-opacity', `${(config.sidebarOpacity ?? 100) / 100}`);
  root.style.setProperty('--header-opacity', `${(config.headerOpacity ?? 100) / 100}`);
  root.style.setProperty('--card-opacity', `${(config.cardOpacity ?? 100) / 100}`);

  // 设置背景图
  if (config.dashboardBgUrl) {
    root.style.setProperty('--dashboard-bg', `url(${config.dashboardBgUrl})`);
  } else {
    root.style.removeProperty('--dashboard-bg');
  }

  if (config.loginBgUrl) {
    root.style.setProperty('--login-bg', `url(${config.loginBgUrl})`);
  } else {
    root.style.removeProperty('--login-bg');
  }

  // 设置主题模式（使用默认值）
  const themeMode = config.themeMode || 'light';
  const actualMode = themeMode === 'auto'
    ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
    : themeMode;

  root.setAttribute('data-theme', actualMode);
  root.classList.remove('light', 'dark');
  root.classList.add(actualMode);
  root.setAttribute('data-compact', String(config.compactMode ?? false));

  // 森林绿 UI 灰度开关：决定是否启用 data-ui="forest"（存量租户默认 default）
  root.setAttribute('data-ui', resolveUiSkin(config));

  // 应用自定义CSS
  if (config.customCss) {
    let styleEl = document.getElementById('tenant-custom-css');
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = 'tenant-custom-css';
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = config.customCss;
  } else {
    const styleEl = document.getElementById('tenant-custom-css');
    if (styleEl) {
      styleEl.remove();
    }
  }

  const themeFaviconUrl = ossProxyUrl(
    resolveTenantFaviconUrl(config.faviconUrl, config.logoUrl)
  );
  // 品牌上下文负责租户 Logo 兜底；主题仅在有真实图标时覆盖，避免历史
  // /vite.svg 配置因加载顺序清除已应用的租户品牌图标。
  if (themeFaviconUrl) {
    applyTenantFavicon(themeFaviconUrl);
  }
}

/**
 * 生成 Ant Design 主题配置
 * 注意：使用camelCase字段名
 */
function generateAntdTheme(config: TenantThemeConfig): AntdThemeConfig {
  if (!config) return {};

  const themeMode = config.themeMode || 'light';
  const isDark = themeMode === 'dark' ||
    (themeMode === 'auto' && typeof window !== 'undefined' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches);

  const algorithms = [
    isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
    config.compactMode ? theme.compactAlgorithm : undefined,
  ].filter(Boolean) as typeof theme.defaultAlgorithm[];

  return {
    algorithm: algorithms,
    token: {
      colorPrimary: config.primaryColor || '#1890ff',
      colorSuccess: config.successColor || '#52c41a',
      colorWarning: config.warningColor || '#faad14',
      colorError: config.errorColor || '#ff4d4f',
      borderRadius: config.borderRadius ?? 6,
      fontSize: config.fontSize ?? 14,
    },
  };
}

/**
 * 从缓存获取主题配置
 */
function getCachedTheme(): TenantThemeConfig | null {
  try {
    const cached = localStorage.getItem(THEME_CACHE_KEY);
    const expiry = localStorage.getItem(THEME_CACHE_EXPIRY_KEY);

    if (cached && expiry) {
      const expiryTime = parseInt(expiry, 10);
      if (Date.now() < expiryTime) {
        return JSON.parse(cached);
      }
    }
  } catch {
    // 忽略解析错误
  }
  return null;
}

/**
 * 缓存主题配置
 */
function cacheTheme(config: TenantThemeConfig): void {
  try {
    localStorage.setItem(THEME_CACHE_KEY, JSON.stringify(config));
    localStorage.setItem(THEME_CACHE_EXPIRY_KEY, String(Date.now() + CACHE_DURATION));
  } catch {
    // 忽略存储错误
  }
}

/**
 * 清除主题缓存
 */
function clearThemeCache(): void {
  try {
    localStorage.removeItem(THEME_CACHE_KEY);
    localStorage.removeItem(THEME_CACHE_EXPIRY_KEY);
  } catch {
    // 忽略错误
  }
}

/**
 * 检查用户是否已登录且有租户ID
 */
function isUserAuthenticated(): boolean {
  const token = getToken();
  if (!token) return false;

  // 检查用户信息中是否有 tenant_id
  const userInfo = getUserInfo();
  if (userInfo?.tenant_id) return true;

  // 从 JWT token 中解析 tenant_id
  try {
    const parts = token.split('.');
    if (parts.length === 3) {
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      return !!payload?.tenant_id;
    }
  } catch {
    // 解析失败
  }

  return false;
}

/**
 * 租户主题管理 Hook
 */
export function useTenantTheme(): UseTenantThemeResult {
  const [themeConfig, setThemeConfig] = useState<TenantThemeConfig | null>(() => getCachedTheme());
  const [loading, setLoading] = useState(!themeConfig);
  const [error, setError] = useState<string | null>(null);
  const [presets, setPresets] = useState<PresetTheme[]>([]);

  // 加载主题配置
  const loadTheme = useCallback(async () => {
    // 如果用户未登录或没有租户ID，跳过加载
    if (!isUserAuthenticated()) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await tenantThemeApi.getTenantTheme();
      if (response.success && response.data) {
        const config = response.data;
        setThemeConfig(config);
        cacheTheme(config);
        applyThemeToDocument(config);
      } else {
        setError(response.message || '加载主题配置失败');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : '加载主题配置失败';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  // 加载预设主题
  const loadPresets = useCallback(async () => {
    // 如果用户未登录或没有租户ID，跳过加载
    if (!isUserAuthenticated()) {
      return;
    }

    try {
      const response = await tenantThemeApi.getPresetThemes();
      if (response.success && response.data) {
        setPresets(response.data);
      }
    } catch (err) {
      console.error('[useTenantTheme] Presets load error:', err);
    }
  }, []);

  // 更新主题
  const updateTheme = useCallback(async (updates: UpdateTenantThemeRequest): Promise<boolean> => {
    try {
      setError(null);
      const response = await tenantThemeApi.updateTenantTheme(updates);
      if (response.success && response.data) {
        const config = response.data;
        setThemeConfig(config);
        cacheTheme(config);
        applyThemeToDocument(config);
        return true;
      }
      setError(response.message || '更新主题失败');
      return false;
    } catch (err) {
      const message = err instanceof Error ? err.message : '更新主题失败';
      setError(message);
      return false;
    }
  }, []);

  // 应用预设主题
  const applyPreset = useCallback(async (presetCode: string): Promise<boolean> => {
    try {
      setError(null);
      const response = await tenantThemeApi.applyPresetTheme(presetCode);
      if (response.success && response.data) {
        const config = response.data;
        setThemeConfig(config);
        cacheTheme(config);
        // REQ-20260619-0002: 同步 ui_skin 到本地，避免旧 localStorage 盖掉预设的界面风格
        syncUiSkinToLocal(config);
        applyThemeToDocument(config);
        return true;
      }
      setError(response.message || '应用预设主题失败');
      return false;
    } catch (err) {
      console.error('[useTenantTheme] Apply preset error:', err);
      const message = err instanceof Error ? err.message : '应用预设主题失败';
      setError(message);
      return false;
    }
  }, []);

  // 重置主题
  const resetTheme = useCallback(async (): Promise<boolean> => {
    try {
      setError(null);
      const response = await tenantThemeApi.resetTenantTheme();
      if (response.success && response.data) {
        const config = response.data;
        setThemeConfig(config);
        clearThemeCache();
        cacheTheme(config);
        // REQ-20260619-0002: 重置也同步 ui_skin（回 default），避免残留森林绿
        syncUiSkinToLocal(config);
        applyThemeToDocument(config);
        return true;
      }
      setError(response.message || '重置主题失败');
      return false;
    } catch (err) {
      const message = err instanceof Error ? err.message : '重置主题失败';
      setError(message);
      return false;
    }
  }, []);

  // 生成 Ant Design 主题配置
  const antdTheme = useMemo<AntdThemeConfig>(() => {
    if (!themeConfig) {
      return {};
    }
    return generateAntdTheme(themeConfig);
  }, [themeConfig]);

  // 初始化加载
  useEffect(() => {
    loadTheme();
    loadPresets();
  }, [loadTheme, loadPresets]);

  // 如果有缓存的主题，立即应用
  useEffect(() => {
    if (themeConfig) {
      applyThemeToDocument(themeConfig);
    }
  }, []);

  // 监听系统主题变化
  useEffect(() => {
    if (themeConfig?.themeMode !== 'auto') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      if (themeConfig) {
        applyThemeToDocument(themeConfig);
      }
    };

    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, [themeConfig]);

  return {
    themeConfig,
    loading,
    error,
    presets,
    updateTheme,
    applyPreset,
    resetTheme,
    reloadTheme: loadTheme,
    antdTheme,
  };
}

export default useTenantTheme;
